import React, { ChangeEvent, FC, useState, useEffect, useRef } from "react";
import { RadioButtonWithLabel } from "../../../../components";
import { Button as TeddyButton, Box, Checkbox, Heading, Icon, Notification, Text } from "@telia/teddy";
import cn from "classnames";
import Markdown from "react-markdown";
import { connect, useDispatch } from "react-redux";
import { CUSTOMER_TYPE, CustomerType } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { AccountMessage } from "../../../../../../models/state";
import { removeWhitespace } from "../../../../../../shared/util";
import { CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER } from "../../../../checkout/state/actionTypes";
import {
    getLegalSubscriptionId,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
    isTeliaTrySubscription as isTeliaTrySubscriptionSelector,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import { setValue as setPersonalDetailsValueAction } from "../../../state/actions";
import * as RETAILER_AT from "../../../state/actionTypes";
import { isCatalogListingOk as isCatalogListingOkSelector } from "../../../state/selectors/customerInfoSelectors";
import { getAvailablePhoneNumbers, getSelectedPanel } from "../../../state/selectors/phoneNumbersSelectors";
import TEXT from "../../../text-consts";
import KeepNumberPanel, { KeepNumberPanelProps } from "../../molecules/KeepNumberPanel/KeepNumberPanel";
import NewNumberPanel, { NewNumberPanelProps } from "../../molecules/NewNumberPanel/NewNumberPanel";
import "./ChoosePhoneNumber.less";
import SubscriptionStepsControls from "../../molecules/SubscriptionStepsControls/SubscriptionStepsControls";

const phoneNumberHeadingId = "retailer-phonenumber-heading";
const options: { label: string; type: CustomerType }[] = [
    {
        type: CUSTOMER_TYPE.PORT_IN,
        label: "Flytt nummer fra en annen operatør",
    },
    {
        type: CUSTOMER_TYPE.NEW,
        label: "Velg nytt nummer",
    },
    {
        type: CUSTOMER_TYPE.EXISTING,
        label: "Behold eksisterende Telia-nummer",
    },
];

const Message = ({ messageLink, logicalMessage }: { logicalMessage: string; messageLink: string }) => {
    if (messageLink) {
        return (
            <div>
                <Markdown>{logicalMessage}</Markdown>
            </div>
        );
    }

    return <div className="paragraph paragraph--fineprint" dangerouslySetInnerHTML={{ __html: logicalMessage }} />;
};

const AccountMessageItem: FC<{ message: AccountMessage }> = ({ message }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const logicalMessage = message.text || "";
    const messageLink = message.link || "";
    const expandedText = message.expandedText;
    const messageIsForNewCustomer = message.isNewCustomerMessage;
    const isMessageSuccess = (message.type as unknown as string) === "success";

    if (!logicalMessage) return null;

    if (messageIsForNewCustomer) {
        return (
            <div className="retailer-choose-phone-number__new-customer-message-container">
                <span className="retailer-choose-phone-number__new-customer-message-icon">
                    <Icon name="smiley-happy" />
                </span>
                <Message messageLink={messageLink} logicalMessage={logicalMessage} />
            </div>
        );
    }

    const getNotificationContent = (): React.ReactNode => {
        if (messageLink) {
            return <Message messageLink={messageLink} logicalMessage={logicalMessage} />;
        }
        if (expandedText) {
            return (
                <div className="paragraph paragraph--fineprint">
                    <p>{isExpanded ? expandedText : logicalMessage}</p>
                    {!isExpanded && (
                        <TeddyButton onClick={() => setIsExpanded(true)} variant="text">
                            Vis mer
                        </TeddyButton>
                    )}
                </div>
            );
        }
        return <div className="paragraph paragraph--fineprint" dangerouslySetInnerHTML={{ __html: logicalMessage }} />;
    };

    return (
        <Notification
            type="tile"
            variant={isMessageSuccess ? "success" : "warning"}
            className="retailer-choose-phone-number__notification"
        >
            <Notification.Icon />
            <Notification.Text>{getNotificationContent()}</Notification.Text>
        </Notification>
    );
};

interface ChoosePhoneNumberOption
    extends
        Omit<KeepNumberPanelProps, "disabled" | "labelId" | "isValidPhoneNumber">,
        Omit<NewNumberPanelProps, "disabled" | "labelId"> {
    disableKeepNumber: boolean;
    disableNewNumber: boolean;
    isValidInputPhoneNumber: boolean;
    type: CustomerType;
}

const ChoosePhoneNumberOption: FC<ChoosePhoneNumberOption> = ({
    type,
    choosePhoneNumber,
    selectedPhoneNumber,
    isSubscriptionBroadband,
    isSubscriptionPrepaid,
    disableKeepNumber,
    disableNewNumber,
    availablePhoneNumbers,
    hideNewPhoneNumberArea,
    hideNewPhoneNumberAreaMessage,
    isFetchingNumbers,
    subscriptionId,
    isValidInputPhoneNumber,
}) => {
    switch (type) {
        case CUSTOMER_TYPE.EXISTING:
        case CUSTOMER_TYPE.PORT_IN:
            return (
                <KeepNumberPanel
                    choosePhoneNumber={choosePhoneNumber}
                    labelId={phoneNumberHeadingId}
                    selectedPhoneNumber={selectedPhoneNumber}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    disabled={disableKeepNumber}
                    isValidPhoneNumber={isValidInputPhoneNumber}
                    data-di-mask
                />
            );
        case CUSTOMER_TYPE.NEW:
            return (
                <NewNumberPanel
                    availablePhoneNumbers={availablePhoneNumbers}
                    choosePhoneNumber={choosePhoneNumber}
                    hideNewPhoneNumberArea={hideNewPhoneNumberArea}
                    hideNewPhoneNumberAreaMessage={hideNewPhoneNumberAreaMessage}
                    isFetchingNumbers={isFetchingNumbers}
                    labelId={phoneNumberHeadingId}
                    selectedPhoneNumber={selectedPhoneNumber}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    isSubscriptionPrepaid={isSubscriptionPrepaid}
                    disabled={disableNewNumber}
                    data-di-mask
                    subscriptionId={subscriptionId}
                />
            );
        default: {
            return null;
        }
    }
};

interface StateProps {
    activePanel: CustomerType;
    availablePhoneNumbers: string[];
    isCatalogListingOk: boolean;
    isCheckingNumber: boolean;
    isFetchingNumbers: boolean;
    isSubscriptionBroadband: boolean;
    isSubscriptionPrepaid: boolean;
    isTeliaTrySubscription: boolean;
}

interface ChoosePhoneNumberProps extends StateProps {
    choosePhoneNumber: (phoneNumber: string, isSubscriptionBroadband: boolean) => void;
    choosePhoneNumberSwitchPanel: (panel: CustomerType) => void;
    disableKeepNumber?: boolean;
    disableNewNumber?: boolean;
    hideNewPhoneNumberArea?: boolean;
    hideNewPhoneNumberAreaMessage?: string;
    ingress?: string;
    isAllowedToProceed?: boolean;
    messages: AccountMessage[];
    onNextButtonClick: () => void;
    selectedPhoneNumber: string;
    showExistingOption?: boolean;
    subscriptionId: string;
}

const ChoosePhoneNumber: FC<ChoosePhoneNumberProps> = ({
    activePanel,
    availablePhoneNumbers,
    choosePhoneNumber,
    choosePhoneNumberSwitchPanel,
    hideNewPhoneNumberArea = false,
    hideNewPhoneNumberAreaMessage = "",
    isFetchingNumbers,
    isCheckingNumber,
    messages,
    selectedPhoneNumber,
    isSubscriptionBroadband,
    isSubscriptionPrepaid,
    isTeliaTrySubscription,
    ingress,
    disableKeepNumber = false,
    disableNewNumber = false,
    showExistingOption = true,
    subscriptionId,
    isAllowedToProceed,
    isCatalogListingOk,
    onNextButtonClick,
}) => {
    const dispatch = useDispatch();
    const [internalErrorMessage, setInternalErrorMessage] = useState("");
    const portInOptionRef = useRef<HTMLDivElement>(null);
    const fortsettButtonRef = useRef<HTMLButtonElement>(null);
    const wasFetchingNewNumbersRef = useRef(false);

    useEffect(() => {
        portInOptionRef.current?.querySelector<HTMLInputElement>("input[type='tel']")?.focus();
    }, []);

    useEffect(() => {
        if (activePanel === CUSTOMER_TYPE.NEW && isFetchingNumbers) {
            wasFetchingNewNumbersRef.current = true;
        } else if (wasFetchingNewNumbersRef.current && activePanel === CUSTOMER_TYPE.NEW && !isFetchingNumbers) {
            wasFetchingNewNumbersRef.current = false;
            fortsettButtonRef.current?.focus();
        }
    }, [isFetchingNumbers, activePanel]);
    const isFirstMessageSuccess = (messages[0]?.type as unknown as string) === "success";

    useEffect(() => {
        if (isFirstMessageSuccess) {
            fortsettButtonRef.current?.focus();
        }
    }, [isFirstMessageSuccess]);
    const onlyDigits = (selectedPhoneNumber || "").replace(/\s/g, "");
    const isValidInputPhoneNumber = onlyDigits.length === 8 && isFirstMessageSuccess;

    const handleNextButtonClick = () => {
        if (isAllowedToProceed) {
            onNextButtonClick();
            setInternalErrorMessage("");
        } else {
            setInternalErrorMessage("Vennligst fullfør et av alternativene nedenfor");
        }
    };

    const handleChoosePhoneNumber = (phoneNumber: string, isBroadband: boolean) => {
        setInternalErrorMessage("");
        return choosePhoneNumber(phoneNumber, isBroadband);
    };

    const getIsSelected = (type: CustomerType): boolean => {
        if (isTeliaTrySubscription) {
            // Only existing and new is shown for try subscription => portin goes under existing panel
            if (type === CUSTOMER_TYPE.EXISTING && activePanel === CUSTOMER_TYPE.PORT_IN) {
                return true;
            }
        }
        return type === activePanel;
    };

    const availableOptions = showExistingOption
        ? options
        : options.filter((option) => option.type !== CUSTOMER_TYPE.EXISTING);

    return (
        <div className="retailer-choose-phone-number">
            <Box width="100%" className="retailer-choose-phone-number__content">
                {internalErrorMessage && (
                    <div className="retailer-choose-phone-number__error-message">
                        <Icon name="error-filled" />
                        <Text mb="100">{internalErrorMessage}</Text>
                    </div>
                )}
                {ingress && (
                    <Text mb="100" className="beta-checkout__ingress">
                        {ingress}
                    </Text>
                )}
                {availableOptions.map(({ type, label }) => {
                    const isSelected = getIsSelected(type);
                    return (
                        <div
                            key={`retailer-choose-phonenumber-option-${type}`}
                            ref={type === CUSTOMER_TYPE.PORT_IN ? portInOptionRef : undefined}
                            className={cn("retailer-choose-phone-number__option", {
                                "retailer-choose-phone-number__option--selected": isSelected,
                            })}
                        >
                            <RadioButtonWithLabel
                                value={type}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const newPanelSide = event.target.value as CustomerType;
                                    if (newPanelSide && newPanelSide !== activePanel) {
                                        setInternalErrorMessage("");
                                        choosePhoneNumberSwitchPanel(newPanelSide);
                                    }
                                }}
                                name="retailer-choose-phonenumber"
                                hasRichContent
                                checked={isSelected}
                            >
                                <Heading
                                    mb="100"
                                    as="h3"
                                    variant="subsection-100"
                                    className="retailer-choose-phone-number__option-heading"
                                >
                                    {label}
                                </Heading>
                            </RadioButtonWithLabel>
                            {isSelected && (
                                <div className="retailer-choose-phone-number__expanded">
                                    <ChoosePhoneNumberOption
                                        type={type}
                                        availablePhoneNumbers={availablePhoneNumbers}
                                        choosePhoneNumber={handleChoosePhoneNumber}
                                        hideNewPhoneNumberArea={hideNewPhoneNumberArea}
                                        hideNewPhoneNumberAreaMessage={hideNewPhoneNumberAreaMessage}
                                        isFetchingNumbers={isFetchingNumbers}
                                        selectedPhoneNumber={selectedPhoneNumber}
                                        isSubscriptionBroadband={isSubscriptionBroadband}
                                        isSubscriptionPrepaid={isSubscriptionPrepaid}
                                        disableNewNumber={disableNewNumber}
                                        data-di-mask
                                        subscriptionId={subscriptionId}
                                        disableKeepNumber={disableKeepNumber}
                                        isValidInputPhoneNumber={isValidInputPhoneNumber}
                                    />
                                    {messages.map((message) => (
                                        <AccountMessageItem
                                            key={`${subscriptionId}-${message.type}-${message.text}`}
                                            message={message}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                <Checkbox className="retailer-choose-phone-number__checkbox">
                    <Checkbox.Trigger
                        checked={!isCatalogListingOk}
                        onCheckedChange={() => {
                            dispatch(setPersonalDetailsValueAction("isCatalogListingOk", !isCatalogListingOk));
                        }}
                    />
                    <span>{TEXT.PERSONAL_DETAILS.NUMBER_SHOULD_NOT_BE_LISTED_IN_CATALOG}</span>
                </Checkbox>

                <SubscriptionStepsControls
                    nextButton={
                        <TeddyButton
                            ref={fortsettButtonRef}
                            className="retailer-choose-phone-number__button"
                            disabled={
                                isCheckingNumber ||
                                isFetchingNumbers ||
                                !selectedPhoneNumber ||
                                removeWhitespace(selectedPhoneNumber)?.length < 8 ||
                                !isAllowedToProceed
                            }
                            loading={isCheckingNumber || isFetchingNumbers}
                            onClick={handleNextButtonClick}
                            variant="primary"
                        >
                            <span>Fortsett</span>
                            {isCheckingNumber && isFetchingNumbers && (
                                <Icon name="arrow-down" className="retailer-choose-phone-number__button-icon" />
                            )}
                        </TeddyButton>
                    }
                />
            </Box>
        </div>
    );
};

const mapStateToProps: (state: RootState, ownProps: { subscriptionId: string }) => StateProps = (
    state: RootState,
    ownProps: { subscriptionId: string },
) => {
    const subscriptionId = ownProps.subscriptionId || getLegalSubscriptionId(state);

    return {
        activePanel: getSelectedPanel(state),
        availablePhoneNumbers: getAvailablePhoneNumbers(state),
        isCheckingNumber: LOADING_SELECTORS.isTypeLoading(state, CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER),
        isFetchingNumbers: LOADING_SELECTORS.isTypeLoading(state, RETAILER_AT.RETAILER_FETCH_AVAILABLE_PHONE_NUMBERS),
        isSubscriptionBroadband: isSubscriptionBroadbandSelector(state, subscriptionId),
        isSubscriptionPrepaid: isSubscriptionPrepaidSelector(state, subscriptionId),
        isTeliaTrySubscription: isTeliaTrySubscriptionSelector(state, subscriptionId),
        isCatalogListingOk: isCatalogListingOkSelector(state),
        subscriptionId,
    };
};

export default connect(mapStateToProps)(ChoosePhoneNumber);
